import { useEffect, createContext, useState, useContext } from 'react';
import { useQueryClient } from 'react-query';
import { Users } from './../api-calls';
import ReactGA from 'react-ga4';
import { userRoles } from '../constants';

const initialUserState = {
  id: null,
  email: '',
  firstName: '',
  lastName: '',
  role: '',
  selectedLearnerId: null, // for coach when selected learner
  facilitatorId: null, // for coaches
};

const AuthContext = createContext({
  user: initialUserState,
  setUser: () => {},
  logout: () => {},
  setOverlayColor: () => {},
});

const getUserInfoFromStorage = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  if (user && user.id) {
    if (user.role === userRoles.LEARNER || user.role === userRoles.COACH) {
      ReactGA.set({
        ...user,
      });
    }
    return user;
  } else {
    return initialUserState;
  }
};

const storeUserInfoIntoStorage = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

const clearUserInfoIntoStorage = (user) => {
  localStorage.removeItem('user');
};

const AuthProvider = (props) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(getUserInfoFromStorage);

  const _setUser = (data) => {
    // set User in local storage
    storeUserInfoIntoStorage(data);
    // set user in state
    setUser(data);
  };

  const getUserInfo = async () => {
    const { data } = await Users.getLoggedInUserInfo();
    if (data) {
      _setUser(data);
    } else {
      clearUserInfoIntoStorage();
      setUser(initialUserState);
      queryClient.removeQueries();
    }
  };

  const getCSRFToken = async () => {
    await Users.getCSRFToken();
  };

  useEffect(() => {
    getUserInfo();
    getCSRFToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async ({ isLearner = false }) => {
    const { error } = await Users.logout();
    if (!error) {
      _setUser({ logout: true, isLearner });
      clearUserInfoIntoStorage();
      queryClient.removeQueries();
    }
  };

  const setOverlayColor = (color) => {
    _setUser({ ...user, overlayColor: color });
  };

  const value = {
    user,
    getUserInfo,
    setUser: _setUser,
    logout,
    setOverlayColor,
  };

  return <AuthContext.Provider value={value} {...props} />;
};

const useAuth = () => {
  const value = useContext(AuthContext);
  return value;
};

export { AuthProvider, useAuth };
export default AuthContext;
